import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Sparkles, Brain, Plug, PlugZap, Send } from "lucide-react";
import Sidebar from "../../Components/Sidebar";
import Topbar from "../../Components/Topbar";
import { RobotIcon } from "../Auth/onboarding/icons";
import {
  summarizeMemory,
  getAssistantConfig,
  getAssistantById,
  listConversations,
  getConversationMessages,
} from "../../actions/assistants";
import { readWidgetTheme } from "../../utils/widgetTheme";
import { connectTraining, sendTrainingMessage } from "../../utils/trainingSocket";
import TrainingThread from "./TrainingThread";

const now = () =>
  new Date().toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });

let entryId = 0;
const nextId = () => {
  entryId += 1;
  return entryId;
};

export default function TrainingAssistant() {
  const assistantId = useSelector((state) => state.assistants.activeId);
  const assistantName = useSelector(
    (state) =>
      state.assistants.list.find(
        (item) => item.id === state.assistants.activeId,
      )?.name || "Your assistant",
  );
  const dispatch = useDispatch();

  const [status, setStatus] = useState("idle");
  const [statusText, setStatusText] = useState("Not connected.");
  const [entries, setEntries] = useState([]);
  const [input, setInput] = useState("");
  const [thinking, setThinking] = useState(false);
  const [summarizing, setSummarizing] = useState(false);
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState("");
  // The widget config supplies the avatar, so training looks like the same
  // assistant the tester and the live widget show.
  const [config, setConfig] = useState(null);
  // The stored transcript, fetched before any socket is opened.
  const [historyLoading, setHistoryLoading] = useState(false);

  const socketRef = useRef(null);
  const conversationRef = useRef(null);

  const avatarUrl = readWidgetTheme(config).avatarUrl;

  const connected = status === "connected";

  const addEntry = useCallback((entry) => {
    setEntries((prev) => [...prev, { id: nextId(), time: now(), ...entry }]);
  }, []);

  // Leaving the page mid-session would otherwise keep the socket open.
  useEffect(
    () => () => {
      socketRef.current?.disconnect();
      socketRef.current = null;
    },
    [],
  );

  // A different assistant is a different training session.
  useEffect(() => {
    socketRef.current?.disconnect();
    socketRef.current = null;
    conversationRef.current = null;
    setStatus("idle");
    setStatusText("Not connected.");
    setEntries([]);
    setSummary(null);
    setError("");
    setConfig(null);
    setHistoryLoading(false);
  }, [assistantId]);

  // Two read-only reads: the config for the avatar, and the record for the
  // memory the assistant has already been given. Without the second the card
  // stays blank until someone presses Summarize, which reads as "it has
  // learned nothing" even when it has.
  useEffect(() => {
    if (!assistantId) return undefined;

    let cancelled = false;

    dispatch(getAssistantConfig(assistantId)).then((result) => {
      if (!cancelled && result?.success) setConfig(result.config);
    });

    dispatch(getAssistantById(assistantId)).then((result) => {
      if (cancelled || !result?.success) return;

      const stored = result.assistant?.memorySummary;
      // count stays null: the record says what it remembers, not how many
      // facts went into it.
      if (stored) setSummary({ count: null, text: stored });
    });

    return () => {
      cancelled = true;
    };
  }, [assistantId, dispatch]);

  // Opens on what was already said rather than an empty box. The list is
  // newest-first, so limit=1 is the most recent training session; its
  // transcript comes from GET .../conversations/:id/messages.
  useEffect(() => {
    if (!assistantId) return undefined;

    let cancelled = false;

    const loadLatest = async () => {
      setHistoryLoading(true);

      const list = await dispatch(
        listConversations(assistantId, {
          source: "training",
          limit: 1,
          page: 1,
        }),
      );

      const first = list?.success ? list.conversations?.[0] : null;
      const conversationId = first?.id || first?._id || "";

      if (cancelled) return;

      if (!conversationId) {
        setHistoryLoading(false);
        return;
      }

      // Sending resumes this conversation rather than starting a new one.
      conversationRef.current = conversationId;

      const transcript = await dispatch(
        getConversationMessages(assistantId, conversationId),
      );

      if (cancelled) return;

      setHistoryLoading(false);

      if (!transcript?.success) return;

      setEntries(
        (transcript.messages || []).map((message) => ({
          id: nextId(),
          kind:
            String(message.role || "").toLowerCase() === "user"
              ? "user"
              : "assistant",
          text: message.content || message.text || message.message || "",
          // Marked as earlier so the thread labels them as history rather
          // than as things said in this session.
          earlier: true,
        })),
      );
    };

    loadLatest();

    return () => {
      cancelled = true;
    };
  }, [assistantId, dispatch]);

  const handleConnect = () => {
    if (!assistantId || socketRef.current) return;

    // The transcript loaded on open names the session to continue. Clearing
    // it here - as this used to - meant every connect asked the server for a
    // fresh conversation and left the previous one orphaned.
    const resumeId = conversationRef.current;

    setError("");
    setSummary(null);
    if (!resumeId) setEntries([]);
    setStatus("connecting");
    setStatusText("Connecting…");

    socketRef.current = connectTraining(assistantId, {
      onStarted: (data) => {
        conversationRef.current = data.conversationId || resumeId || null;
        setStatus("connected");
        setStatusText(
          conversationRef.current
            ? "Training session ready."
            : "Connected — say something to start.",
        );

        // The server's copy of the thread wins, but only when it sends one:
        // replacing rather than appending keeps a transcript already fetched
        // over REST from being listed twice.
        const history = data.messages || [];

        if (history.length) {
          setEntries(
            history.map((message) => ({
              id: nextId(),
              time: now(),
              kind: message.role === "user" ? "user" : "assistant",
              text: message.content,
              earlier: true,
            })),
          );
        }
      },

      onCreated: (data) => {
        conversationRef.current = data.conversationId || null;
        setStatusText("Training session created.");
      },

      onStage: (data) => {
        if (data.stage === "fact_learned") {
          setThinking(false);
          addEntry({
            kind: "learned",
            question: data.question || "",
            text: data.answer || "",
          });
          return;
        }

        // Everything else is progress noise; show it as a live status rather
        // than a permanent line in the transcript.
        setThinking(true);
        setStatusText(
          data.tool ? `${data.stage} (${data.tool})` : String(data.stage || ""),
        );
      },

      onFinal: (data) => {
        setThinking(false);
        setStatusText("Training session ready.");
        addEntry({ kind: "assistant", text: data.reply });
      },

      onError: (message, fatal) => {
        setThinking(false);
        setError(message);
        if (fatal) {
          setStatus("idle");
          setStatusText("Connection rejected.");
          socketRef.current = null;
        }
      },

      onDisconnect: () => {
        setThinking(false);
        setStatus("idle");
        setStatusText("Disconnected.");
        socketRef.current = null;
      },
    }, resumeId);
  };

  const handleDisconnect = () => {
    socketRef.current?.disconnect();
    socketRef.current = null;
    setStatus("idle");
    setStatusText("Disconnected.");
  };

  const handleSend = (e) => {
    e?.preventDefault();

    const text = input.trim();
    if (!text || !connected) return;

    addEntry({ kind: "user", text });
    sendTrainingMessage(socketRef.current, conversationRef.current, text);
    setInput("");
    setThinking(true);
  };

  const handleSummarize = async () => {
    if (!assistantId || summarizing) return;

    setSummarizing(true);
    setError("");

    const result = await dispatch(summarizeMemory(assistantId));

    setSummarizing(false);

    if (!result?.success) {
      setError(result?.message || "We couldn't summarize what was learned.");
      return;
    }

    setSummary({
      count: result.factsSummarized,
      text: result.memorySummary,
    });
  };

  const learnedCount = entries.filter((item) => item.kind === "learned").length;

  if (!assistantId) {
    return (
      <div className="dash">
        <Sidebar />
        <div className="dash-main">
          <Topbar
            title="Training assistant"
            subtitle="Teach your assistant facts about your business by talking to it."
          />
          <div className="dash-content">
            <div className="asst-list-state">
              <span className="asst-list-empty-icon">
                <RobotIcon />
              </span>
              <strong>No assistant selected</strong>
              <p>Pick one from the switcher at the top of the sidebar.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dash">
      <Sidebar />

      <div className="dash-main">
        <Topbar
          title="Training assistant"
          subtitle="Teach your assistant facts about your business by talking to it."
        />

        <div className="dash-content">
          {error && <p className="auth-error">{error}</p>}

          <div className="train-grid">
            <section className="dash-card train-chat-card">
              <div className="train-header">
                <span className="train-avatar">
                  {avatarUrl ? (
                    <img src={avatarUrl} alt="" />
                  ) : (
                    <Brain size={20} strokeWidth={1.9} />
                  )}
                </span>
                <div className="train-header-text">
                  <strong>Training {assistantName}</strong>
                  <span className={`train-status is-${status}`}>
                    <span className="train-status-dot" />
                    {statusText}
                  </span>
                </div>

                {connected ? (
                  <button
                    type="button"
                    className="onboarding__btn onboarding__btn--secondary train-conn-btn"
                    onClick={handleDisconnect}
                  >
                    <PlugZap size={16} strokeWidth={1.9} />
                    Disconnect
                  </button>
                ) : (
                  <button
                    type="button"
                    className="onboarding__btn onboarding__btn--primary train-conn-btn"
                    onClick={handleConnect}
                    disabled={status === "connecting"}
                  >
                    <Plug size={16} strokeWidth={1.9} />
                    {status === "connecting" ? "Connecting…" : "Start training"}
                  </button>
                )}
              </div>

              <TrainingThread
                entries={entries}
                loading={historyLoading}
                thinking={thinking}
                connected={connected}
                assistantName={config?.name || assistantName}
                avatarUrl={avatarUrl}
              />

              <form className="train-input-row" onSubmit={handleSend}>
                <input
                  type="text"
                  placeholder={
                    connected
                      ? "Tell your assistant something about your business…"
                      : "Start training to begin"
                  }
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  disabled={!connected}
                />
                <button
                  type="submit"
                  className="train-send-btn"
                  disabled={!connected || !input.trim()}
                  aria-label="Send"
                >
                  <Send size={17} strokeWidth={2} />
                </button>
              </form>
            </section>

            <div className="train-side-col">
              <section className="dash-card train-memory-card">
                <div className="dash-card-header">
                  <h2 className="cz-card-title">Memory</h2>
                  {learnedCount > 0 && (
                    <span className="bill-badge bill-badge--active">
                      {learnedCount} learned
                    </span>
                  )}
                </div>
                <p className="ist-card-subtitle">
                  Facts picked up in this session are stored as they happen.
                  Summarising folds them into one memory your assistant reads
                  before every answer.
                </p>

                <button
                  type="button"
                  className="onboarding__btn onboarding__btn--secondary train-summarize-btn"
                  onClick={handleSummarize}
                  disabled={summarizing}
                >
                  {summarizing ? (
                    <span className="install-status-spinner" aria-hidden="true" />
                  ) : (
                    <Sparkles size={16} strokeWidth={1.9} />
                  )}
                  {summarizing ? "Summarizing…" : "Summarize memory"}
                </button>

                {summary && (
                  <div className="train-summary">
                    <span className="train-summary-count">
                      {summary.count === null
                        ? "Current memory"
                        : `${summary.count} fact${summary.count === 1 ? "" : "s"} summarized`}
                    </span>
                    <p>{summary.text || "No memory stored yet."}</p>
                  </div>
                )}
              </section>

              <section className="dash-card train-tips-card">
                <h2 className="cz-card-title">What to teach it</h2>
                <ul className="train-tips">
                  <li>Opening hours, holidays and response times.</li>
                  <li>Pricing, plans and what each one includes.</li>
                  <li>Which questions should go to a human.</li>
                  <li>Anything the website doesn&apos;t say out loud.</li>
                </ul>
              </section>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
